import { useState, useEffect, useCallback } from 'react';

export interface CmsMediaRecord {
  id: string;
  mediaType: 'image' | 'video';
  usage: string[];
  tags: string[];
  category?: string;
  cloudinaryUrl: string;
  cloudinaryURL: string;
  cloudinaryPublicId?: string;
  width?: number;
  height?: number;
  format?: string;
  fileHash?: string;
  fileSize?: number;
  products: string[];
  usageCount: number;
  altTexts?: Record<string, { altText: string | null; title: string | null }>;
  createdAt?: string;
}

/**
 * CMS-specific media hook that fetches from /api/cms/media (staging DB).
 *
 * The global MediaContext fetches from the public API which reads the
 * production DB on production deployments. CMS pages need staging media
 * to match the staging products they display.
 */
export function useCmsMedia() {
  const [media, setMedia] = useState<CmsMediaRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchMedia = useCallback(async () => {
    try {
      const token = localStorage.getItem('cms_token');
      if (!token) return;

      const response = await fetch(`/api/cms/media?_t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        console.warn('[useCmsMedia] fetch failed:', response.status, response.statusText);
        return;
      }

      const data = await response.json();
      // Normalize: CMS API returns cloudinaryUrl, some consumers expect cloudinaryURL
      setMedia((data.media || []).map((m: any) => ({
        ...m,
        cloudinaryURL: m.cloudinaryUrl,
        tags: m.tags || [],
        fileHash: m.fileHash || null,
        fileSize: m.fileSize || null,
        altTexts: m.altTexts || {},
      })));
    } catch (err) {
      console.error('[useCmsMedia] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const getMediaByProduct = useCallback((productId: string): CmsMediaRecord[] => {
    if (!productId) return [];

    // First try matching by product record_id in the media's products array
    let matches = media.filter(m => m.products?.includes(productId));

    // Fallback: match by product ID prefix in media ID
    if (matches.length === 0) {
      const normalizedProductId = productId.toUpperCase();
      const segments = normalizedProductId.split('-');

      for (let i = segments.length; i > 1; i--) {
        const prefix = segments.slice(0, i).join('-');
        matches = media.filter(m => {
          if (!m.id) return false;
          return m.id.toUpperCase().startsWith(prefix + '-');
        });
        if (matches.length > 0) break;
      }
    }

    // Deduplicate by URL + usage
    return Array.from(
      new Map(matches.map(m => {
        const usageKey = Array.isArray(m.usage) ? m.usage.sort().join(',') : (m.usage || '');
        return [`${m.cloudinaryUrl}|${usageKey}`, m];
      })).values()
    );
  }, [media]);

  const refreshMedia = useCallback(async () => {
    await fetchMedia();
  }, [fetchMedia]);

  return { media, loading, getMediaByProduct, refreshMedia };
}
